import React from 'react';
import { observer } from 'mobx-react';
import { action } from 'mobx';
import { CommonPopupBodyFooterLayout } from '../../../../../../global/common-popup/common-popup-body-footer-layout';
import { DefaultCloseSubWindowButton } from '../../../../../../global/sub-window/common/common-close-sub-window-buttons';
import '../emails-template-form-presentation.scss';
import { FormField } from '../../../../../../global/form/form-field/form-field';
import { UploadImageS3Presentation } from '../../../../shared/upload-image/upload-image-to-s3.presentation';
import { RichTextEditor } from '../../../../../../global/rich-text-editor';
import CreateEmailTemplateViewModel from './create-email-template-form.viewmodel';
import { FormattedMessage } from 'react-intl';
import { configForGoogleCalendar } from '../../../../../../global/rich-text-editor/CKEditor.config';
import { EmailTemplateType } from '../../../../../../../api/email-configuration.dao';
import { CANCEL_TEXT, LOGO_IMAGE_TEXT } from '../../../../../../../constants/common-labels.constants';

interface IProps {
    vm: CreateEmailTemplateViewModel;
}

const CreateEmailsTemplateFormPresentation = observer((props: IProps) => {
    const { vm } = props;
    const { emailTemplateFormVM } = vm;
    const { intl } = emailTemplateFormVM.props;
    const isCombinationNotUnique = vm.isTemplateCombinationUnique === false;
    const shouldDisableActions = !vm.isTemplateCombinationUnique;

    const subjectLabel = vm.isCalendarInvitationTemplateType
        ? <FormattedMessage
            id='oe.label.invitationSubject'
            description='Label text'
            defaultMessage='Invitation Subject'
        />
        : <FormattedMessage
            id='oe.label.emailSubject'
            description='Label text'
            defaultMessage='Email Subject'
        />
    ;
    const bodyLabel = vm.isCalendarInvitationTemplateType
        ? <FormattedMessage
            id='oe.label.invitationBody'
            description='Label text'
            defaultMessage='Invitation Body'
        />
        : <FormattedMessage
            id='oe.label.emailBody'
            description='Label text'
            defaultMessage='Email Body'
        />
    ;

    return (
        <CommonPopupBodyFooterLayout
            body={
                <>
                    <div
                        className='emails-template-form m-top-0px p-top-0px'
                    >
                        <div className='oe-form'>
                            <div className='oe-form__body'>
                                <div className='emails-template-form__select-wrapper'>
                                    <FormField
                                        label={intl.formatMessage({
                                            id: 'oe.website.responsiveEmails.startingTemplate',
                                            defaultMessage: 'Starting Template',
                                            description: 'Title text'
                                        })}
                                    >
                                        <select
                                            className='oe-form-field__control'
                                            value={vm.emailTemplatePlaceholder.type}
                                            onChange={e => { vm.setSelectedStartingTemplate(e.target.value); }}
                                        >
                                            <option value=''>
                                                {intl.formatMessage({
                                                    id: 'oe.website.responsiveEmails.selectStartingTemplate',
                                                    description: 'Label text',
                                                    defaultMessage: 'Select Starting Template',
                                                })}
                                            </option>
                                            {
                                                emailTemplateFormVM.availableTypes.map(({ name, type }, i) => {
                                                    return <option value={type} key={`${i}_${type}`}>{name}</option>;
                                                })
                                            }
                                        </select>
                                    </FormField>
                                    <FormField
                                        label={intl.formatMessage({
                                            id: 'oe.website.responsiveEmails.triageQuestionAnswer',
                                            defaultMessage: 'Triage Question Answer',
                                            description: 'Title text'
                                        })}
                                    >
                                        <select
                                            className='oe-form-field__control'
                                            value={vm.emailTemplatePlaceholder.subtype}
                                            onChange={e => { vm.setSelectedSubType(e.target.value); }}
                                        >
                                            <option value=''>
                                                {intl.formatMessage({
                                                    id: 'oe.website.responsiveEmails.selectTriageQuestionAnswer',
                                                    description: 'Label text',
                                                    defaultMessage: 'Select Triage Question Answer',
                                                })}
                                            </option>
                                            {
                                                emailTemplateFormVM.availableSubtypes.map((subType, i) => {
                                                    return <option value={subType} key={`${i}_${subType}`}>{subType}</option>;
                                                })
                                            }
                                        </select>
                                    </FormField>
                                </div>
                                {isCombinationNotUnique &&
                                    <span className='oe-form-field__error m-bottom-25px'>
                                        {vm.intl.formatMessage({
                                            id: `oe.triageAnswer.${vm.selectedStartingTemplateName}.${vm.emailTemplatePlaceholder.subtype}`,
                                            description: 'Error message',
                                            defaultMessage: `An email template for
"${vm.selectedStartingTemplateName} - ${vm.emailTemplatePlaceholder.subtype}"
already exists. Please select a different template or answer, or edit the existing saved template.`
                                        })}
                                    </span>
                                }
                                {vm.currentTemplateType !== EmailTemplateType.REGISTRATION_CALENDAR_INVITATION && <UploadImageS3Presentation
                                    vm={vm.uploadNewEmailLogoImageS3VM}
                                    fieldName={intl.formatMessage(LOGO_IMAGE_TEXT)}
                                />}
                                <div className={'emails-template-form__content'}>
                                    <FormField
                                        className='m-top-5px'
                                        label={subjectLabel}
                                        labelFor='create-email-template-subject'
                                    >
                                        <div className='flex'>
                                            <input
                                                disabled={shouldDisableActions}
                                                readOnly={shouldDisableActions}
                                                id='create-email-template-subject'
                                                className='oe-form-field__control'
                                                value={vm.emailTemplatePlaceholder.subject || ''}
                                                onChange={e => shouldDisableActions
                                                    ? undefined
                                                    : vm.setEmailSubject(e.target.value)
                                                }
                                                type='text'
                                            />
                                        </div>
                                        {!!vm.getSelectedTemplateSubjectError
                                            && <span className='oe-form-field__error'>{vm.getSelectedTemplateSubjectError}</span>
                                        }
                                    </FormField>

                                    <FormField
                                        label={bodyLabel}
                                        className='with-editor'
                                    >
                                        <RichTextEditor
                                            className='m-top-0px body-editor'
                                            value={vm.emailTemplatePlaceholder.body || ''}
                                            onChange={action((_, editor) => {
                                                vm.setEmailBody(editor.getData());
                                            })}
                                            disabled={shouldDisableActions}
                                            validationError={vm.emailTemplatesErrorHandler.emailBodyError ?? null}
                                            config={vm.isCalendarInvitationTemplateType ? configForGoogleCalendar : undefined}
                                        />
                                    </FormField>
                                    {vm.isCalendarInvitationTemplateType &&
                                        <FormField
                                            className='m-top-5px'
                                            label={<FormattedMessage
                                                id='oe.label.location'
                                                description='Label text'
                                                defaultMessage='Location'
                                            />}
                                            labelFor='emails-template-location'
                                        >
                                            <div className='flex'>
                                                <input
                                                    id='emails-template-location'
                                                    className='oe-form-field__control'
                                                    value={vm.emailTemplatePlaceholder.location || ''}
                                                    onChange={e => vm.setTemplateEmailLocation(e.target.value)}
                                                    type='text'
                                                    disabled={shouldDisableActions}
                                                />
                                            </div>
                                            {vm.templateEmailLocationError
                                                && <span className='oe-form-field__error'>{vm.templateEmailLocationError}</span>
                                            }
                                        </FormField>
                                    }
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            }
            footer={
                <>
                    {vm.isSaving &&
                        <span className='oe-popup-layout__footer-status-area'>
                            {intl.formatMessage({
                                id: 'oe.website.registrationEmailIsBeingSaved',
                                description: 'Placeholder about saving account',
                                defaultMessage: 'Registration Email is being saved, please wait...'
                            })}
                        </span>
                    }

                    <div className='buttons-block buttons-block--from-right'>
                        <DefaultCloseSubWindowButton
                            text={intl.formatMessage({
                                id: 'oe.common.add',
                                description: 'label text',
                                defaultMessage: 'Add'
                            })}
                            modifier={['fat', 'confirm-safe']}
                            onClick={vm.onCreateEmailTemplate}
                            disabled={vm.isSaving || !vm.isFormValid}
                        />
                        <DefaultCloseSubWindowButton
                            text={intl.formatMessage(CANCEL_TEXT)}
                        />
                    </div>
                </>
            }
        />
    );
});

export { CreateEmailsTemplateFormPresentation };
